import { FormikErrors } from 'formik';
import {
  HiringTeamUsersFindManyQuery,
  ProjectStageAssessmentStatusFilter,
  ProjectStageTypeFilterArgs,
} from 'generated/graphql';
import { TMultiselectOption } from '@spotted-zebra-uk/ui-components';
import { FiltersFormValues } from './ProjectsFilterModal';
import {
  ASSESSMENT_STATUS_CATEGORY,
  AssessmentStatusFilter,
  AtsLinkedFilter,
  emptyStageTypeFilterValues,
  FilterableStageCategory,
  FilterFieldName,
  INTERVIEW_LINKED_CATEGORY,
  InterviewLinkedFilter,
  PROJECTS_FILTER_TABS,
  STAGE_TYPE_FILTER_CATEGORIES,
  StageTypeFilterValue,
  StageTypeFilterValues,
  StageTypePresence,
} from './ProjectsFilterModal.constants';

/**
 * The status options that carry a rule. Any is left out: it is the absence of
 * the filter, so it has nothing to map to.
 */
const ASSESSMENT_STATUS_ARGS: Record<
  Exclude<AssessmentStatusFilter, AssessmentStatusFilter.Any>,
  ProjectStageAssessmentStatusFilter
> = {
  [AssessmentStatusFilter.NotStarted]:
    ProjectStageAssessmentStatusFilter.NotStarted,
  [AssessmentStatusFilter.InProgress]:
    ProjectStageAssessmentStatusFilter.InProgress,
  [AssessmentStatusFilter.Completed]:
    ProjectStageAssessmentStatusFilter.Completed,
};

const hasFieldError = (
  errors: FormikErrors<FiltersFormValues>,
  fieldName: FilterFieldName
): boolean => Boolean(errors[fieldName]);

/** Whether any field on this tab failed validation. */
export const hasTabError = (
  errors: FormikErrors<FiltersFormValues>,
  tabIndex: number
): boolean =>
  PROJECTS_FILTER_TABS[tabIndex].fieldNames.some(fieldName =>
    hasFieldError(errors, fieldName)
  );

/**
 * The leftmost tab holding an invalid field, or -1 when everything validates.
 * Apply switches to it so the inline error is on screen rather than hidden
 * behind a tab the user cannot see.
 */
export const getFirstInvalidTabIndex = (
  errors: FormikErrors<FiltersFormValues>
): number =>
  PROJECTS_FILTER_TABS.findIndex((filterTab) =>
    filterTab.fieldNames.some((fieldName) => hasFieldError(errors, fieldName))
  );

/**
 * Turns the query's rows into multiselect options.
 *
 * The option's `id` carries the company the user was found under, so a
 * selection can be dropped synchronously when that company is deselected —
 * see {@link pruneHiringTeamUsers}. `value` is the user id, which is what the
 * filter sends.
 */
export const toHiringTeamUserOptions = (
  hiringTeamUsers?: HiringTeamUsersFindManyQuery['HiringTeamUsersFindMany']
): TMultiselectOption[] =>
  (hiringTeamUsers ?? []).map((hiringTeamUser) => ({
    label: `${hiringTeamUser.firstName} ${hiringTeamUser.lastName}`.trim(),
    value: String(hiringTeamUser.id),
    labelNote: hiringTeamUser.companyName,
    id: hiringTeamUser.companyId,
  }));

/**
 * Drops selected users whose company is no longer selected. Applied both when
 * a company is deselected and when a shared URL is loaded, so a link can never
 * filter by someone outside the companies it also names.
 */
export const pruneHiringTeamUsers = (
  hiringTeamUsers: TMultiselectOption[],
  companies: TMultiselectOption[]
): TMultiselectOption[] => {
  const selectedCompanyIds = new Set(
    companies.map((company) => String(company.value))
  );

  return hiringTeamUsers.filter((hiringTeamUser) =>
    selectedCompanyIds.has(String(hiringTeamUser.id))
  );
};

/**
 * The segmented control's three-way value as the API's optional boolean:
 * "Any" is the absence of the filter, not a value.
 */
export const toAtsLinkedFilterArg = (
  atsLinked: AtsLinkedFilter
): boolean | undefined => {
  if (atsLinked === AtsLinkedFilter.Yes) return true;
  if (atsLinked === AtsLinkedFilter.No) return false;
  return undefined;
};

/**
 * The stage rows from a URL, with anything they are missing filled in from the
 * defaults.
 *
 * A filter URL is shareable and outlives the shape it was written in: one saved
 * before the minimum was renamed and the assessment status added carries
 * neither, which would otherwise reach the form as `undefined` and leave its
 * fields uncontrolled.
 */
export const withStageTypeFilterDefaults = (
  stageTypeFilters?: Partial<
    Record<FilterableStageCategory, Partial<StageTypeFilterValue>>
  >
): StageTypeFilterValues => {
  const defaults = emptyStageTypeFilterValues();

  if (!stageTypeFilters) return defaults;

  return STAGE_TYPE_FILTER_CATEGORIES.reduce(
    (values, category) => ({
      ...values,
      [category]: { ...defaults[category], ...stageTypeFilters[category] },
    }),
    {} as StageTypeFilterValues
  );
};

/**
 * The stage rows as API rules. Rows left on "Any" carry no rule, a blank
 * minimum means "just needs a stage of this type", and an assessment status is
 * only sent for the one category that offers it.
 */
export const toStageTypeFilterArgs = (
  stageTypeFilters: StageTypeFilterValues
): ProjectStageTypeFilterArgs[] =>
  STAGE_TYPE_FILTER_CATEGORIES.flatMap((category) => {
    const { presence, minCandidates, assessmentStatus, interviewLinked } =
      stageTypeFilters[category];
    if (presence === StageTypePresence.Any) return [];

    const hasStageType = presence === StageTypePresence.Has;
    const hasAssessmentStatus =
      hasStageType &&
      category === ASSESSMENT_STATUS_CATEGORY &&
      assessmentStatus !== AssessmentStatusFilter.Any;
    const hasInterviewLinked =
      hasStageType &&
      category === INTERVIEW_LINKED_CATEGORY &&
      interviewLinked !== InterviewLinkedFilter.Any;

    return [
      {
        category,
        hasStageType,
        ...(hasStageType && minCandidates
          ? { minCandidates: Number(minCandidates) }
          : {}),
        ...(hasAssessmentStatus
          ? { assessmentStatus: ASSESSMENT_STATUS_ARGS[assessmentStatus] }
          : {}),
        ...(hasInterviewLinked
          ? { interviewLinked: interviewLinked === InterviewLinkedFilter.Yes }
          : {}),
      },
    ];
  });
